import React, { useState, useEffect } from 'react';
import { 
  X, 
  FileCheck, 
  Upload, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  MinusCircle,
  ChevronDown,
  ChevronUp,
  Trash2,
  FileText,
  Building2,
  User
} from 'lucide-react';
import { screeningService, documentService } from '../services/api';

// Moved outside to prevent re-creation on every render
const StatusBadge = ({ status }) => {
  const styles = {
    VERIFIED: { bg: '#dcfce7', color: '#166534', icon: CheckCircle, label: 'Verified' },
    IN_PROGRESS: { bg: '#dbeafe', color: '#1e40af', icon: Clock, label: 'In Progress' },
    NOT_STARTED: { bg: '#f1f5f9', color: '#475569', icon: AlertCircle, label: 'Not Started' },
    NOT_APPLICABLE: { bg: '#fef3c7', color: '#92400e', icon: MinusCircle, label: 'N/A' },
  };
  const style = styles[status] || styles.NOT_STARTED;
  const Icon = style.icon;

  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '4px 10px',
      borderRadius: '9999px',
      fontSize: '12px',
      fontWeight: '500',
      backgroundColor: style.bg,
      color: style.color,
    }}>
      <Icon size={12} />
      {style.label}
    </span>
  );
};

// Moved outside to prevent re-creation on every render (fixes input focus issue)
const ChecklistItem = ({ 
  item, 
  isExpanded, 
  isUploading, 
  onToggleExpand, 
  onStatusChange, 
  onPercentageChange,
  onFileUpload, 
  onDeleteDocument 
}) => {
  // Local state for percentage input
  const [percentageInput, setPercentageInput] = useState(item.percentage_value || '');
  
  // Sync local state when item data changes (e.g., after save)
  useEffect(() => {
    setPercentageInput(item.percentage_value || '');
  }, [item.percentage_value]);

  return (
    <div style={{
      border: '1px solid #e2e8f0',
      borderRadius: '8px',
      marginBottom: '12px',
      overflow: 'hidden',
      backgroundColor: 'white',
    }}>
      {/* Item Header */}
      <div 
        onClick={() => onToggleExpand(item.id)}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '14px 16px',
          cursor: 'pointer',
          backgroundColor: isExpanded ? '#f8fafc' : 'white',
        }}
      >
        <div style={{ flex: 1 }}>
          <p style={{ 
            margin: 0, 
            fontSize: '14px', 
            fontWeight: '500',
            color: '#1e293b' 
          }}>
            {item.item_description}
          </p>
          {item.documents.length > 0 && (
            <p style={{ 
              margin: '4px 0 0 0', 
              fontSize: '12px', 
              color: '#64748b' 
            }}>
              {item.documents.length} document(s) attached
            </p>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <StatusBadge status={item.status} />
          {isExpanded ? <ChevronUp size={18} color="#64748b" /> : <ChevronDown size={18} color="#64748b" />}
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div style={{
          padding: '16px',
          borderTop: '1px solid #e2e8f0',
          backgroundColor: '#f8fafc',
        }}>
          {/* Status Actions */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ 
              display: 'block', 
              fontSize: '12px', 
              fontWeight: '600', 
              color: '#475569',
              marginBottom: '8px' 
            }}>
              Update Status
            </label>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {['NOT_STARTED', 'IN_PROGRESS', 'VERIFIED', 'NOT_APPLICABLE'].map(status => (
                <button
                  key={status}
                  onClick={(e) => {
                    e.stopPropagation();
                    onStatusChange(item.id, status);
                  }}
                  style={{
                    padding: '6px 12px',
                    fontSize: '12px',
                    border: item.status === status ? '2px solid #3b82f6' : '1px solid #d1d5db',
                    borderRadius: '6px',
                    backgroundColor: item.status === status ? '#dbeafe' : 'white',
                    color: item.status === status ? '#1e40af' : '#475569',
                    cursor: 'pointer',
                    fontWeight: item.status === status ? '600' : '400',
                  }}
                >
                  {status.replace('_', ' ')}
                </button>
              ))}
            </div>
          </div>

          {/* Percentage Input for B-BBEE Ownership */}
          {item.item_code === 'BBBEE_OWNERSHIP' && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '12px', 
                fontWeight: '600', 
                color: '#475569',
                marginBottom: '8px' 
              }}>
                Ownership Percentage
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={percentageInput}
                  onChange={(e) => setPercentageInput(e.target.value)}
                  onBlur={(e) => {
                    if (e.target.value !== (item.percentage_value || '').toString()) {
                      onPercentageChange(item.id, e.target.value);
                    }
                  }}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    width: '120px',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                  }}
                  placeholder="0.00"
                />
                <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: '500' }}>%</span>
              </div>
            </div>
          )}

          {/* Document Upload */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ 
              display: 'block', 
              fontSize: '12px', 
              fontWeight: '600', 
              color: '#475569',
              marginBottom: '8px' 
            }}>
              Documents
            </label>
            
            {/* Upload Button */}
            <label style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 14px',
              backgroundColor: '#3b82f6',
              color: 'white',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '500',
              cursor: isUploading ? 'not-allowed' : 'pointer',
              opacity: isUploading ? 0.7 : 1,
            }}>
              <Upload size={14} />
              {isUploading ? 'Uploading...' : 'Upload Document'}
              <input
                type="file"
                style={{ display: 'none' }}
                disabled={isUploading}
                onChange={(e) => {
                  if (e.target.files[0]) {
                    onFileUpload(item.id, e.target.files[0]);
                  }
                }}
              />
            </label>
          </div>

          {/* Document List */}
          {item.documents.length > 0 && (
            <div>
              {item.documents.map(doc => (
                <div 
                  key={doc.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '10px 12px',
                    backgroundColor: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    marginBottom: '8px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <FileText size={18} color="#3b82f6" />
                    <div>
                      <p style={{ margin: 0, fontSize: '13px', fontWeight: '500', color: '#1e293b' }}>
                        {doc.document_name}
                      </p>
                      <p style={{ margin: '2px 0 0 0', fontSize: '11px', color: '#64748b' }}>
                        Uploaded by {doc.uploaded_by_name} • {new Date(doc.uploaded_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const fileUrl = doc.document_file.startsWith('http') 
                          ? doc.document_file 
                          : `http://127.0.0.1:8000${doc.document_file}`;
                        window.open(fileUrl, '_blank', 'noopener,noreferrer');
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '6px 10px',
                        backgroundColor: '#dbeafe',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '500',
                        color: '#1e40af',
                      }}
                    >
                      Download
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteDocument(doc.id);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '6px',
                        backgroundColor: '#fee2e2',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                      }}
                    >
                      <Trash2 size={14} color="#dc2626" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Verification Info */}
          {item.status === 'VERIFIED' && item.verified_by_name && (
            <div style={{
              marginTop: '12px',
              padding: '10px 12px',
              backgroundColor: '#dcfce7',
              borderRadius: '6px',
              fontSize: '12px',
              color: '#166534',
            }}>
              Verified by {item.verified_by_name} on {new Date(item.verified_at).toLocaleDateString()}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const SectionHeader = ({ icon: Icon, title, items }) => {
  const verified = items.filter(i => i.status === 'VERIFIED').length;
  const total = items.length;
  
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      marginBottom: '16px',
      marginTop: '24px',
    }}>
      <div style={{
        width: '32px',
        height: '32px',
        backgroundColor: '#dbeafe',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <Icon size={16} color="#3b82f6" />
      </div>
      <div>
        <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: '#1e293b' }}>
          {title}
        </h3>
        <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>
          {verified}/{total} verified
        </p>
      </div>
    </div>
  );
};

function SupplierScreening({ supplier, onClose }) {
  const [checklistItems, setChecklistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});
  const [uploadingItem, setUploadingItem] = useState(null);

  useEffect(() => {
    fetchChecklist();
  }, [supplier.id]);

  const fetchChecklist = async () => {
    try {
      setLoading(true);
      const response = await screeningService.getBySupplier(supplier.id);
      const data = response.data.results || response.data;
      setChecklistItems(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      setError('Failed to load screening checklist');
      console.error('Error fetching checklist:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (itemId, newStatus) => {
    try {
      await screeningService.updateStatus(itemId, newStatus);
      fetchChecklist();
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update status');
    }
  };

  const handlePercentageChange = async (itemId, value) => {
    try {
      await screeningService.updatePercentage(itemId, value === '' ? null : value);
      fetchChecklist();
    } catch (err) {
      console.error('Error updating percentage:', err);
      alert('Failed to update percentage');
    }
  };

  const handleFileUpload = async (itemId, file) => {
    try {
      setUploadingItem(itemId);
      
      const formData = new FormData();
      formData.append('document_file', file);
      formData.append('document_name', file.name);
      formData.append('supplier', supplier.id);
      formData.append('screening_item', itemId);
      
      await documentService.upload(formData);
      fetchChecklist();
    } catch (err) {
      console.error('Error uploading document:', err);
      alert('Failed to upload document');
    } finally {
      setUploadingItem(null);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    
    try {
      await documentService.delete(docId);
      fetchChecklist();
    } catch (err) {
      console.error('Error deleting document:', err);
      alert('Failed to delete document');
    }
  };

  const toggleExpand = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  // Calculate progress
  const totalItems = checklistItems.length;
  const verifiedItems = checklistItems.filter(i => i.status === 'VERIFIED').length;
  const naItems = checklistItems.filter(i => i.status === 'NOT_APPLICABLE').length;
  const completedItems = verifiedItems + naItems;
  const progressPercent = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

  // Group items by type
  const entityItems = checklistItems.filter(i => i.verification_type === 'ENTITY');
  const individualItems = checklistItems.filter(i => i.verification_type === 'INDIVIDUAL');

  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
      }}>
        <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '12px' }}>
          Loading screening checklist...
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      padding: '40px 20px',
      overflowY: 'auto',
      zIndex: 1000,
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        width: '100%',
        maxWidth: '800px',
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '20px 24px',
          borderBottom: '1px solid #e2e8f0',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '44px',
              height: '44px',
              backgroundColor: '#dbeafe',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <FileCheck size={22} color="#3b82f6" />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>
                Supplier Screening
              </h2>
              <p style={{ margin: 0, fontSize: '14px', color: '#64748b' }}>
                {supplier.supplier_name}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
            }}
          >
            <X size={20} color="#64748b" />
          </button>
        </div>

        {/* Progress Bar */}
        <div style={{ padding: '16px 24px', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ fontSize: '13px', fontWeight: '500', color: '#475569' }}>
              Screening Progress
            </span>
            <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>
              {completedItems}/{totalItems} complete ({progressPercent}%)
            </span>
          </div>
          <div style={{
            height: '8px',
            backgroundColor: '#e2e8f0',
            borderRadius: '4px',
            overflow: 'hidden',
          }}>
            <div style={{
              height: '100%',
              width: `${progressPercent}%`,
              backgroundColor: progressPercent === 100 ? '#22c55e' : '#3b82f6',
              borderRadius: '4px',
              transition: 'width 0.3s ease',
            }} />
          </div>
          <div style={{ display: 'flex', gap: '16px', marginTop: '8px' }}>
            <span style={{ fontSize: '12px', color: '#22c55e' }}>✓ {verifiedItems} verified</span>
            <span style={{ fontSize: '12px', color: '#f59e0b' }}>○ {naItems} N/A</span>
            <span style={{ fontSize: '12px', color: '#64748b' }}>○ {totalItems - completedItems} remaining</span>
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: '0 24px 24px', overflowY: 'auto', flex: 1 }}>
          {error && (
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#fee2e2',
              color: '#991b1b',
              borderRadius: '8px',
              marginTop: '16px',
            }}>
              {error}
            </div>
          )}

          {/* Entity Verification Section */}
          <SectionHeader icon={Building2} title="Entity Verification" items={entityItems} />
          {entityItems.map(item => (
            <ChecklistItem 
              key={item.id} 
              item={item}
              isExpanded={expandedItems[item.id]}
              isUploading={uploadingItem === item.id}
              onToggleExpand={toggleExpand}
              onStatusChange={handleStatusChange}
              onPercentageChange={handlePercentageChange}
              onFileUpload={handleFileUpload}
              onDeleteDocument={handleDeleteDocument}
            />
          ))}

          {/* Individual Verification Section */}
          <SectionHeader icon={User} title="Individual Verification" items={individualItems} />
          {individualItems.map(item => (
            <ChecklistItem 
              key={item.id} 
              item={item}
              isExpanded={expandedItems[item.id]}
              isUploading={uploadingItem === item.id}
              onToggleExpand={toggleExpand}
              onStatusChange={handleStatusChange}
              onPercentageChange={handlePercentageChange}
              onFileUpload={handleFileUpload}
              onDeleteDocument={handleDeleteDocument}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default SupplierScreening;